import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Mail, Phone, MapPin, PawPrint, FileText, Receipt as ReceiptIcon,
  RefreshCcw, Plus, Wallet, AlertCircle, CheckCircle, Users, Activity, Send, Eye
} from 'lucide-react';
import { logger } from '../../utils/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtMoney = (n, currency = 'CI$') =>
  `${currency}${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(); } catch { return iso; }
};

const statusPill = (status) => {
  const s = (status || '').toLowerCase();
  const styles = {
    draft: 'bg-gray-100 text-gray-700',
    pending: 'bg-yellow-100 text-yellow-800',
    sent: 'bg-blue-100 text-blue-800',
    accepted: 'bg-emerald-100 text-emerald-800',
    declined: 'bg-red-100 text-red-800',
    paid: 'bg-green-100 text-green-800',
    overdue: 'bg-red-100 text-red-800',
    cancelled: 'bg-gray-100 text-gray-700',
    completed: 'bg-green-100 text-green-800',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${styles[s] || 'bg-gray-100 text-gray-700'}`}>
      {s ? s.charAt(0).toUpperCase() + s.slice(1) : '—'}
    </span>
  );
};

export const ViewClient = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const password = sessionStorage.getItem('admin_password');
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!password) {
      navigate('/admin/login');
      return;
    }
    fetchSummary();
  }, [id]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/clients/${id}/summary`, {
        headers: { Authorization: `Basic ${btoa(':' + password)}` },
      });
      if (res.status === 404) {
        setError('Client not found');
        setLoading(false);
        return;
      }
      if (!res.ok) throw new Error('Failed');
      setSummary(await res.json());
    } catch (err) {
      logger.error('Client summary error:', err);
      setError('Failed to load client');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mt-16" />
      </div>
    );
  }
  if (error || !summary) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <button onClick={() => navigate('/admin/clients')} className="text-sm text-primary flex items-center gap-2 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back to Clients
        </button>
        <p className="text-red-600">{error || 'No data'}</p>
      </div>
    );
  }

  const { client, quotes, invoices, receipts, refunds, pets, totals, timeline = [] } = summary;
  const displayName = client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim() || client.email;

  const prefilledQuoteUrl = `/admin/quotes/new?client_email=${encodeURIComponent(client.email || '')}`;
  const prefilledInvoiceUrl = `/admin/invoices/new?client_email=${encodeURIComponent(client.email || '')}`;

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Header */}
      <div className="bg-white border-b border-border">
        <div className="container mx-auto px-6 py-5">
          <button
            onClick={() => navigate('/admin/clients')}
            className="text-sm text-foreground/70 hover:text-foreground inline-flex items-center gap-2 mb-3"
            data-testid="view-client-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Clients
          </button>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground" data-testid="client-display-name">{displayName}</h1>
              <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground/70">
                {client.email && (
                  <span className="inline-flex items-center gap-1.5"><Mail className="w-4 h-4" /> {client.email}</span>
                )}
                {client.phone && (
                  <span className="inline-flex items-center gap-1.5"><Phone className="w-4 h-4" /> {client.phone}</span>
                )}
                {client.address && (
                  <span className="inline-flex items-center gap-1.5"><MapPin className="w-4 h-4" /> {client.address}</span>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => navigate(prefilledQuoteUrl)}
                data-testid="quick-new-quote"
                className="px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> New Quote
              </button>
              <button
                onClick={() => navigate(prefilledInvoiceUrl)}
                data-testid="quick-new-invoice"
                className="px-3 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 inline-flex items-center gap-2"
              >
                <Plus className="w-4 h-4" /> New Invoice
              </button>
              <button
                onClick={() => navigate(`/admin/clients/${encodeURIComponent(client.email)}/emails`)}
                className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-gray-50 inline-flex items-center gap-2"
              >
                <Mail className="w-4 h-4" /> Email History
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard icon={<Wallet className="w-5 h-5" />} label="Lifetime Spend" value={fmtMoney(totals.invoices_paid_total)} tone="green" testid="stat-lifetime-spend" />
          <StatCard icon={<AlertCircle className="w-5 h-5" />} label="Outstanding" value={fmtMoney(totals.outstanding_total)} tone={totals.outstanding_total > 0 ? 'red' : 'muted'} testid="stat-outstanding" />
          <StatCard icon={<CheckCircle className="w-5 h-5" />} label="Paid Invoices" value={String(totals.invoices_count)} tone="blue" testid="stat-invoices-count" />
          <StatCard icon={<Users className="w-5 h-5" />} label="Pets on File" value={String(totals.pets_count)} tone="muted" testid="stat-pets-count" />
        </div>

        {/* Timeline */}
        <Section
          title="Activity Timeline"
          count={timeline.length}
          icon={<Activity className="w-5 h-5 text-primary" />}
          empty="No activity yet."
        >
          <div className="p-5" data-testid="client-timeline">
            <ol className="relative border-l border-border ml-3 space-y-4">
              {timeline.slice(0, 30).map((ev) => (
                <TimelineItem key={ev.id} event={ev} navigate={navigate} />
              ))}
            </ol>
            {timeline.length > 30 && (
              <p className="mt-4 text-xs text-foreground/50 text-center">Showing 30 most recent of {timeline.length} events.</p>
            )}
          </div>
        </Section>

        {/* Quotes */}
        <Section
          title="Quotes"
          count={quotes.length}
          icon={<FileText className="w-5 h-5 text-primary" />}
          empty="No quotes yet for this client."
        >
          {quotes.map(q => (
            <RowLink key={q.id} onClick={() => navigate(`/admin/quotes/${q.id}`)} testid={`view-quote-${q.id}`}>
              <span className="font-mono text-sm text-foreground/80">{q.quote_number}</span>
              <span className="text-sm">{fmtDate(q.quote_date)}</span>
              <span>{statusPill(q.status)}</span>
              <span className="font-semibold">{fmtMoney(q.total, q.currency)}</span>
            </RowLink>
          ))}
        </Section>

        {/* Invoices */}
        <Section
          title="Invoices"
          count={invoices.length}
          icon={<FileText className="w-5 h-5 text-primary" />}
          empty="No invoices yet for this client."
        >
          {invoices.map(i => (
            <RowLink key={i.id} onClick={() => navigate(`/admin/invoices/${i.id}`)} testid={`view-invoice-${i.id}`}>
              <span className="font-mono text-sm text-foreground/80">{i.invoice_number}</span>
              <span className="text-sm">{fmtDate(i.invoice_date)}</span>
              <span>{statusPill(i.status)}</span>
              <span className="font-semibold">{fmtMoney(i.total, i.currency)}</span>
            </RowLink>
          ))}
        </Section>

        {/* Receipts */}
        <Section
          title="Receipts"
          count={receipts.length}
          icon={<ReceiptIcon className="w-5 h-5 text-primary" />}
          empty="No receipts yet for this client."
        >
          {receipts.map(r => (
            <RowLink key={r.id} onClick={() => navigate(`/admin/receipts/${r.id}`)} testid={`view-receipt-${r.id}`}>
              <span className="font-mono text-sm text-foreground/80">{r.receipt_number}</span>
              <span className="text-sm">{fmtDate(r.receipt_date)}</span>
              <span className="text-sm text-foreground/60">{r.payment_method || '—'}</span>
              <span className="font-semibold">{fmtMoney(r.amount_paid, r.currency)}</span>
            </RowLink>
          ))}
        </Section>

        {/* Refunds */}
        <Section
          title="Refunds"
          count={refunds.length}
          icon={<RefreshCcw className="w-5 h-5 text-primary" />}
          empty="No refunds for this client."
        >
          {refunds.map(r => (
            <RowLink key={r.id} onClick={() => navigate(`/admin/refunds/${r.id}`)} testid={`view-refund-${r.id}`}>
              <span className="font-mono text-sm text-foreground/80">{r.refund_number}</span>
              <span className="text-sm">{fmtDate(r.refund_date)}</span>
              <span>{statusPill(r.status)}</span>
              <span className="font-semibold">{fmtMoney(r.amount, r.currency)}</span>
            </RowLink>
          ))}
        </Section>

        {/* Pets */}
        <Section
          title="Pets on File"
          count={pets.length}
          icon={<PawPrint className="w-5 h-5 text-primary" />}
          empty="No pets on file yet."
        >
          {pets.map(p => (
            <RowLink key={p.id} onClick={() => navigate(`/admin/pets/${p.id}`)} testid={`view-pet-${p.id}`}>
              <span className="font-medium">{p.name}</span>
              <span className="text-sm text-foreground/60">{p.species || '—'} · {p.breed || '—'}</span>
              <span className="text-sm text-foreground/60">{fmtDate(p.dob)}</span>
              <span className="text-sm text-foreground/40">→</span>
            </RowLink>
          ))}
        </Section>
      </div>
    </div>
  );
};

const toneClasses = {
  green: 'bg-green-50 text-green-700 border-green-200',
  red: 'bg-red-50 text-red-700 border-red-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  muted: 'bg-gray-50 text-foreground/70 border-border',
};

const StatCard = ({ icon, label, value, tone = 'muted', testid }) => (
  <div className={`p-4 rounded-xl border ${toneClasses[tone] || toneClasses.muted}`} data-testid={testid}>
    <div className="flex items-center gap-2 text-xs uppercase tracking-wide opacity-80">
      {icon}
      <span>{label}</span>
    </div>
    <div className="mt-2 text-xl font-bold">{value}</div>
  </div>
);

const Section = ({ title, count, icon, empty, children }) => (
  <div className="bg-white rounded-xl border border-border overflow-hidden">
    <div className="px-5 py-3 border-b border-border flex items-center gap-2">
      {icon}
      <h2 className="font-semibold text-foreground">{title}</h2>
      <span className="ml-auto text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">{count}</span>
    </div>
    {count === 0 ? (
      <p className="p-5 text-sm text-foreground/50">{empty}</p>
    ) : (
      <div className="divide-y divide-border">{children}</div>
    )}
  </div>
);

const RowLink = ({ children, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className="w-full grid grid-cols-2 md:grid-cols-4 items-center gap-3 px-5 py-3 hover:bg-gray-50 text-left"
  >
    {children}
  </button>
);

const KIND_META = {
  quote_created:      { icon: FileText,       dot: 'bg-blue-500',    goto: (e) => `/admin/quotes/${e.ref_id}` },
  quote_converted:    { icon: RefreshCcw,     dot: 'bg-indigo-500',  goto: (e) => `/admin/quotes/${e.ref_id}` },
  invoice_created:    { icon: FileText,       dot: 'bg-amber-500',   goto: (e) => `/admin/invoices/${e.ref_id}` },
  invoice_paid:       { icon: CheckCircle,    dot: 'bg-green-500',   goto: (e) => `/admin/invoices/${e.ref_id}` },
  receipt_issued:     { icon: ReceiptIcon,    dot: 'bg-emerald-500', goto: (e) => `/admin/receipts/${e.ref_id}` },
  refund_created:     { icon: RefreshCcw,     dot: 'bg-rose-500',    goto: (e) => `/admin/refunds/${e.ref_id}` },
  pet_added:          { icon: PawPrint,       dot: 'bg-primary',     goto: (e) => `/admin/pets/${e.ref_id}` },
  email_sent:         { icon: Send,           dot: 'bg-sky-500',     goto: null },
  email_failed:       { icon: AlertCircle,    dot: 'bg-red-500',     goto: null },
  email_opened:       { icon: Eye,            dot: 'bg-emerald-500', goto: null },
};

const TimelineItem = ({ event, navigate }) => {
  const meta = KIND_META[event.kind] || { icon: Activity, dot: 'bg-gray-400', goto: null };
  const Icon = meta.icon;
  const clickable = !!meta.goto;
  const onClick = clickable ? () => navigate(meta.goto(event)) : undefined;

  return (
    <li className="ml-4">
      <span className={`absolute -left-1.5 w-3 h-3 rounded-full ${meta.dot}`} />
      <button
        type="button"
        onClick={onClick}
        disabled={!clickable}
        data-testid={`timeline-event-${event.ref_type || event.kind}-${event.ref_id || ''}`}
        className={`w-full text-left flex items-start gap-3 py-1 ${clickable ? 'hover:bg-gray-50 rounded-lg px-2 -mx-2 cursor-pointer' : 'cursor-default'}`}
      >
        <Icon className="w-4 h-4 mt-0.5 text-foreground/60 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <p className="text-sm font-medium text-foreground truncate">{event.title}</p>
            {event.amount != null && (
              <span className="text-xs text-foreground/60 font-mono flex-shrink-0">
                {event.currency || 'CI$'}{Number(event.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
          </div>
          {event.description && (
            <p className="text-xs text-foreground/60 mt-0.5 truncate">{event.description}</p>
          )}
          <p className="text-[11px] text-foreground/40 mt-0.5">{fmtDate(event.at)}</p>
        </div>
      </button>
    </li>
  );
};

export default ViewClient;
